import React from 'react';
import { useTranslation } from 'react-i18next';
import _upperFirst from 'lodash/upperFirst';
import _toLower from 'lodash/toLower';
import _startCase from 'lodash/startCase';
import {
  Documentation,
  DocumentationValidationStatusEnum,
} from 'graphql-common';
import Chip, { ChipSizes } from '@lib/components/Chip/Chip';
import ChipWithDropDown from '@lib/components/ChipWithDropDown/ChipWithDropDown';
import Menu, { NavItemTypeEnum } from '@lib/components/Menu/Menu';
import getValidationStatusOptions from '@lib/utils/documentation/getValidationStatusOptions';
import getValidationStatusChipType from '@lib/utils/documentation/getValidationStatusChipType';

type Props = {
  documentation: Documentation;
  loading?: boolean;
  onValidationStatusChangeHandler?: (
    id: string,
    newStatus: DocumentationValidationStatusEnum,
  ) => void;
};

export default function DocumentationValidationStatusChip(props: Props) {
  const { t } = useTranslation();
  const { documentation, loading, onValidationStatusChangeHandler } = props;
  const { id, validationStatus } = documentation;
  const options = getValidationStatusOptions();

  if (!validationStatus) return <>-</>;

  let label: string | undefined;
  const selectedOption = options.find(
    ({ value }) => value === validationStatus,
  );
  label = selectedOption?.labelKey ? t(selectedOption.labelKey) : undefined;
  if (label === undefined)
    label = _upperFirst(_toLower(_startCase(validationStatus)));

  const type = getValidationStatusChipType(validationStatus);

  if (onValidationStatusChangeHandler !== undefined) {
    const chipLeftIcon = (statusItem: DocumentationValidationStatusEnum) => (
      <Chip
        leftIcon="circle"
        type={getValidationStatusChipType(statusItem)}
        size={ChipSizes.smallLeftIconOnly}
      />
    );
    const getBody = () => (
      <Menu
        navItems={options
          .filter(({ value }) => value !== validationStatus)
          .map(({ value, labelKey }) => ({
            id: value,
            title: t(labelKey),
            onClick: () => onValidationStatusChangeHandler(id, value),
            type: NavItemTypeEnum.Secondary,
            leftIcon: () => chipLeftIcon(value),
          }))}
      />
    );
    return (
      <div>
        <ChipWithDropDown
          label={label}
          type={type}
          leftIcon="circle"
          width={180}
          body={getBody}
          disabled={loading}
        />
      </div>
    );
  }

  return <Chip type={type} label={label} leftIcon="circle" asBlockElement />;
}
